// frontend/src/hooks/useMyTeamOvertimeData.js
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import toast from '../utils/toast';

/**
 * Manager-scoped ("My Team") overtime monitoring data hook.
 *
 * Employee-facing twin of `pages/OvertimeMonitoring/hooks/useOvertimeData`.
 * Hits `/staff-core/overtime-monitoring/my-team/employees/`, which returns
 * the SAME payload shape as the admin endpoint but scoped server-side to
 * the requesting RO / HOD's span of control (the workmen under their care).
 *
 * Caching model (stale-while-revalidate):
 *   - React Query holds the result with a 15-minute `staleTime` matching
 *     the backend snapshot cache, so revisiting the tab within the window
 *     paints instantly with NO loading modal. After the window, the cached
 *     data stays on screen while a background refetch runs silently.
 *   - The heavy "Loading Team Overtime" modal is therefore gated on
 *     `loading` (= first load only), never on background `isRefetching`.
 *   - The backend rebuilds the company-wide snapshot at most once per
 *     15 min (shared across all managers); a manual refresh forces a
 *     fresh compute via `force_refresh=true`.
 *
 * Differences from the admin hook:
 *   - No alerts / coverage / compliance-report fetching (read-only,
 *     HR-only concerns).
 *   - Surfaces a `forbidden` flag when the backend returns 403 (the user
 *     is not an RO/HOD) so the page can render an empty state instead of
 *     an error toast.
 *
 * The small `deriveStats` / `applyClientFilters` pure helpers are kept
 * local (rather than imported from the admin hook) so this employee-facing
 * surface stays decoupled from the admin page — they mirror that hook's
 * logic deliberately.
 */
const STALE_TIME_MS = 15 * 60 * 1000; // matches backend snapshot staleness
const GC_TIME_MS = 30 * 60 * 1000;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const deriveStatsFromEmployees = (employees, yearMonth) => {
  const list = employees || [];
  const total = list.length;
  const totalOvertimeHours = round2(
    list.reduce((sum, e) => sum + (Number(e.currentOtHours) || 0), 0)
  );
  const averageOvertimeHours = total > 0 ? round2(totalOvertimeHours / total) : 0;

  const byStatus = {
    compliant: 0, at_risk: 0, critical: 0, exceeded: 0,
    projected_exceed: 0, watch: 0, on_track: 0,
  };
  for (const emp of list) {
    const key = emp.unifiedStatus || emp.riskLevel || 'compliant';
    if (key in byStatus) byStatus[key] += 1;
    else byStatus.compliant += 1;
  }

  return {
    currentMonth: {
      yearMonth,
      totalEmployees: total,
      totalOvertimeHours,
      averageOvertimeHours,
    },
    atRiskCount: byStatus.at_risk,
    criticalCount: byStatus.critical,
    exceededCount: byStatus.exceeded,
    compliantCount: byStatus.compliant + byStatus.on_track,
    complianceRate:
      total > 0
        ? Math.round(((byStatus.compliant + byStatus.on_track) / total) * 100)
        : 0,
    projectedExceedCount: byStatus.projected_exceed,
    watchCount: byStatus.watch,
  };
};

const applyClientFilters = (employees, filters) => {
  let list = employees || [];

  if (filters.department) {
    const q = filters.department.toLowerCase();
    list = list.filter((e) => (e.department || '').toLowerCase().includes(q));
  }
  if (filters.trade) {
    const q = filters.trade.toLowerCase();
    list = list.filter((e) => (e.trade || '').toLowerCase().includes(q));
  }
  if (filters.search) {
    const q = filters.search.toLowerCase();
    list = list.filter(
      (e) =>
        (e.staffId || '').toLowerCase().includes(q) ||
        (e.fullName || '').toLowerCase().includes(q) ||
        (e.firstName || '').toLowerCase().includes(q) ||
        (e.lastName || '').toLowerCase().includes(q)
    );
  }
  if (filters.riskLevel) {
    if (filters.riskLevel === 'action_required') {
      list = list.filter((e) => ['exceeded', 'critical'].includes(e.unifiedStatus));
    } else if (filters.riskLevel === 'watch_list') {
      list = list.filter((e) =>
        ['at_risk', 'projected_exceed', 'watch'].includes(e.unifiedStatus)
      );
    } else {
      list = list.filter((e) => e.unifiedStatus === filters.riskLevel);
    }
  }
  if (filters.hasDiscrepancy === 'true') {
    list = list.filter((e) => !!e.hasUndeclaredOt);
  } else if (filters.hasDiscrepancy === 'false') {
    list = list.filter((e) => !e.hasUndeclaredOt);
  }

  return list;
};

const retryFn = (failureCount, error) => {
  const status = error?.response?.status;
  if (status === 403 || status === 400 || status === 404) return false;
  return failureCount < 2;
};

const useMyTeamOvertimeData = () => {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

  const [filters, setFilters] = useState({
    department: '',
    riskLevel: '',
    search: '',
    hasDiscrepancy: '',
    trade: '',
  });

  // One-shot flag consumed by the queryFn so a manual refresh can bypass
  // the backend snapshot cache without changing the React Query key.
  const forceRef = useRef(false);

  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  const query = useQuery({
    queryKey: ['myTeamOvertime', selectedMonth],
    queryFn: async () => {
      const params = { year_month: selectedMonth };
      if (forceRef.current) {
        params.force_refresh = 'true';
        forceRef.current = false;
      }
      const response = await api.getMyTeamOvertimeStatus(params);
      return response.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: retryFn,
    placeholderData: (prev) => prev, // keep prior month visible while switching
  });

  const forbidden = query.error?.response?.status === 403;

  // Toast only for genuine failures — a 403 is the "no team" empty state,
  // handled silently by the panel.
  const lastToastedError = useRef(null);
  useEffect(() => {
    const err = query.error;
    if (err && !forbidden && lastToastedError.current !== err) {
      lastToastedError.current = err;
      console.error('Error fetching my-team overtime status:', err);
      toast.error('Failed to load overtime data');
    }
    if (!err) lastToastedError.current = null;
  }, [query.error, forbidden]);

  const data = query.data;
  const allEmployees = useMemo(
    () => (forbidden ? [] : data?.employees || []),
    [data, forbidden]
  );
  const isCurrentMonth = data?.isCurrentMonth !== false;
  const forecastSummary = data?.forecastSummary || null;
  const cacheStatus = data?.cacheStatus || null;

  const refetch = useCallback(
    (opts = {}) => {
      if (opts.force) forceRef.current = true;
      return query.refetch();
    },
    [query]
  );

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const changeMonth = useCallback((newMonth) => {
    setSelectedMonth(newMonth);
  }, []);

  // Stateless per-month fetch for the multi-month Excel export modal.
  const fetchEmployeesForMonth = useCallback(async (yearMonth, filterParams = {}) => {
    const params = { year_month: yearMonth };
    if (filterParams.department) params.department = filterParams.department;
    if (filterParams.trade) params.trade = filterParams.trade;
    if (filterParams.riskLevel) params.risk_level = filterParams.riskLevel;
    if (filterParams.hasDiscrepancy) params.has_discrepancy = filterParams.hasDiscrepancy;
    if (filterParams.search) params.search = filterParams.search;
    const response = await api.getMyTeamOvertimeStatus(params);
    return {
      employees: response.data.employees || [],
      isCurrentMonth: response.data.isCurrentMonth !== false,
    };
  }, []);

  const getEmployeeHistory = useCallback(async (staffId) => {
    try {
      const response = await api.get(`/staff-core/overtime-monitoring/employee/${staffId}/history/`);
      return response.data;
    } catch (err) {
      console.error('Error fetching employee history:', err);
      toast.error('Failed to load employee history');
      return null;
    }
  }, []);

  const getEmployeeDailyBreakdown = useCallback(async (staffId, yearMonth) => {
    try {
      const params = yearMonth ? { year_month: yearMonth } : {};
      const response = await api.get(
        `/staff-core/overtime-monitoring/employee/${staffId}/daily-breakdown/`,
        { params }
      );
      return response.data;
    } catch (err) {
      console.error('Error fetching employee daily breakdown:', err);
      toast.error('Failed to load daily OT breakdown');
      return null;
    }
  }, []);

  const stats = useMemo(
    () => deriveStatsFromEmployees(allEmployees, selectedMonth),
    [allEmployees, selectedMonth]
  );

  const filteredEmployees = useMemo(
    () => applyClientFilters(allEmployees, filters),
    [allEmployees, filters]
  );

  return {
    stats,
    employees: filteredEmployees,
    allEmployees,
    forecastSummary,
    // First load only — background revalidation must NOT re-pop the modal.
    loading: query.isLoading,
    isRefetching: query.isRefetching,
    error: forbidden ? null : query.error,
    forbidden,
    filters,
    selectedMonth,
    isCurrentMonth,
    monthOptions,
    cacheStatus,
    refetch,
    updateFilters,
    changeMonth,
    fetchEmployeesForMonth,
    getEmployeeHistory,
    getEmployeeDailyBreakdown,
  };
};

/**
 * Cheap eligibility pre-check for the "My Team" Overtime tab.
 *
 * 72-hour overtime monitoring is workmen-only. A manager can have a
 * non-empty span of control (e.g. only `staff`-class direct reports) yet
 * zero overtime-eligible workmen — in which case the OT roster is always
 * empty and the tab is pointless. The dashboard calls this BEFORE rendering
 * the Overtime tab so it can hide the tab entirely rather than land the
 * user on an empty state (the S0133 case).
 *
 * Returns `{ data: { has_overtime_team, team_workmen_count }, ... }`.
 * A 403 (manages nobody) resolves to `has_overtime_team: false`.
 */
export const useMyTeamOvertimeEligibility = () => {
  return useQuery({
    queryKey: ['myTeamOvertime', 'eligibility'],
    queryFn: async () => {
      try {
        const response = await api.getMyTeamOvertimeEligibility();
        return response.data;
      } catch (err) {
        if (err?.response?.status === 403) {
          return { has_overtime_team: false, team_workmen_count: 0 };
        }
        throw err;
      }
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: retryFn,
  });
};

export default useMyTeamOvertimeData;
